import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, X } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DatePicker } from '@/components/DatePicker'

type Row = {
  account_id: number
  code: string
  name: string
  account_type: string
  debit: string
  credit: string
  balance: string
  balance_side: 'DEBIT' | 'CREDIT'
}

type TB = {
  from: string | null
  to: string | null
  rows: Row[]
  total_debit: string
  total_credit: string
  is_balanced: boolean
}

const TYPE_LABEL: Record<string, string> = {
  ASSET: 'Asset', LIABILITY: 'Liability', EQUITY: 'Equity',
  REVENUE: 'Revenue', COST_OF_SALES: 'COGS', EXPENSE: 'Expense',
}

export default function ReportTrialBalance() {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['report', 'tb', from, to],
    queryFn: async () => {
      const params: any = {}
      if (from) params.from = from
      if (to) params.to = to
      return (await api.get<TB>('/finance/reports/trial-balance/', { params })).data
    },
  })

  return (
    <AdminLayout>
      <h2 className="text-2xl font-bold text-gray-800 mb-1">Trial Balance</h2>
      <p className="text-sm text-gray-500 mb-6">
        Every account with activity in the date range, showing debits and credits.
        Total debits must equal total credits.
      </p>

      <div className="flex items-end space-x-3 mb-4 bg-white p-3 rounded-lg shadow">
        <Field>
          <FieldLabel htmlFor="tb-from">From</FieldLabel>
          <DatePicker id="tb-from" value={from} onChange={(v) => setFrom(v)} />
        </Field>
        <Field>
          <FieldLabel htmlFor="tb-to">To</FieldLabel>
          <DatePicker id="tb-to" value={to} onChange={(v) => setTo(v)} />
        </Field>
        <Button type="button" variant="ghost" onClick={() => { setFrom(''); setTo('') }}>Reset</Button>
        {data && (
          <div className="ml-auto text-sm">
            <span className={`inline-flex items-center gap-1 ${data.is_balanced ? 'text-green-700' : 'text-red-700'}`}>
              {data.is_balanced
                ? (<><Check className="h-4 w-4" /> Balanced</>)
                : (<><X className="h-4 w-4" /> NOT balanced</>)}
            </span>
          </div>
        )}
      </div>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : !data || data.rows.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No journal activity in this period.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Type</Th>
                <Th className="text-right">Debit</Th>
                <Th className="text-right">Credit</Th>
                <Th className="text-right">Balance</Th>
                <Th>Side</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.rows.map((r) => (
                <tr key={r.account_id} className="hover:bg-gray-50">
                  <Td className="font-mono">{r.code}</Td>
                  <Td>{r.name}</Td>
                  <Td className="text-xs text-gray-500">{TYPE_LABEL[r.account_type]}</Td>
                  <Td className="text-right font-mono">{Number(r.debit) > 0 ? Number(r.debit).toFixed(2) : ''}</Td>
                  <Td className="text-right font-mono">{Number(r.credit) > 0 ? Number(r.credit).toFixed(2) : ''}</Td>
                  <Td className="text-right font-mono font-medium">{Number(r.balance).toFixed(2)}</Td>
                  <Td className="text-xs">{r.balance_side}</Td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 font-semibold">
              <tr>
                <td colSpan={3} className="px-4 py-2 text-right text-sm">Totals</td>
                <td className="px-4 py-2 text-right font-mono">{Number(data.total_debit).toFixed(2)}</td>
                <td className="px-4 py-2 text-right font-mono">{Number(data.total_credit).toFixed(2)}</td>
                <td className="px-4 py-2 text-right font-mono">
                  {(Number(data.total_debit) - Number(data.total_credit)).toFixed(2)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </AdminLayout>
  )
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide ${className || ''}`}>{children}</th>
}
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-sm ${className || ''}`}>{children}</td>
}
